import { autoinject, BindingEngine, computedFrom, transient } from "aurelia-framework";
import * as moment from "moment";
import { DateExpressionParser } from "../../../../../configuration/date-expressions";
import { AllEntityTypeConfigurationConstants, QueryLookupsWithEF, StepModel } from "../../../../../models/basics/basics.models";
import { BaseRuleConfigParameterModel } from "../../../../../models/customers/customer.models";
import { LookupValue } from "../../../../../models/lookups/lookup.models";
import { DataType, QueryFilter } from "../../../../../models/queries/queries.models";
import { VettingType } from "../../../../../models/vetting/vetting.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../services/basics/notification-client.service";
import { ManageRightShipUsersClientService } from "../../../../../services/modules/righship-qi-manage-users.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { UnitOfWorkAction, UnitOfWorkItem, UnitOfWorkObserver } from "../../../../../utilities/unit-of-work-observer";
import { EditableFieldBinding, GridUtils } from "../../../../components/data-grid/data-grid";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

const vettingRuleTitle: string = 'Vetting Rule';

@autoinject()
@transient()
export class EditRuleParametersDialog {

    constructor(private dialogClientService: DialogClientService, private bindingEngine: BindingEngine,
        private notificationClientService: NotificationClientService, private manageRightShipUsersClientService: ManageRightShipUsersClientService, 
        private configService: ConfigService) {
    }

    currentTab: StepModel = null;
    tabs: StepModel[] = [
        {
            lazyLoad: true,
            show: () => this.isVettingRule,
            title: vettingRuleTitle,
            step: 1,
            onClick: async () => {
                const parameter = this.parameters.filter(x => x.vettingTypeId == VettingType.FullVet)[0];
                if (parameter)
                    this.startTracking(parameter);
            }
        },
        {
            lazyLoad: true,
            show: () => this.isCriticalRule,
            title: "Screening - Critical Rule",
            step: 2,
            onClick: async () => {
                const parameter = this.parameters.filter(x => x.vettingTypeId == VettingType.AutoScreening && x.isCritical)[0];
                if (parameter)
                    this.startTracking(parameter);
            }
        },
        {
            lazyLoad: true,
            show: () => this.isSoftRule,
            title: "Screening - Soft Rule",
            step: 3,
            onClick: async () => {
                const parameter = this.parameters.filter(x => x.vettingTypeId == VettingType.AutoScreening && !x.isCritical)[0];
                if (parameter)
                    this.startTracking(parameter);
            }
        }
    ];

    attached() {
        this.currentTab = this.tabs.filter(x => (<any>x.show)())[0];
        this.currentTab.onClick(null);
    }

    uow: UnitOfWorkObserver;
    parameters: BaseRuleConfigParameterModel[];
    ruleConfigParameters: LookupValue[] = [];
    isReadOnly: boolean;
    customerId: number;
    businessUnitId: number;
    isVettingRule: boolean;
    isCriticalRule: boolean;
    isSoftRule: boolean;
    parameter: BaseRuleConfigParameterModel;
    ghgSelect = ((e, item) => { e.stopPropagation(); item.showGhgRatingFilter = !item.showGhgRatingFilter; })

    itemSource: { name: string; id: number; mapping: string; }[] = [
        { name: "days", id: 1, mapping: "today" },
        { name: "weeks", id: 1, mapping: "week" },
        { name: "months", id: 2, mapping: "month" },
        { name: "years", id: 3, mapping: "year" }
    ];


    @computedFrom("currentTab", "isSoftRule", "isCriticalRule")
    get showCopy() {
        return this.currentTab && this.currentTab.title != vettingRuleTitle && (this.isSoftRule || this.isCriticalRule) && this.isVettingRule;
    }

    activate(model: { isReadOnly: boolean, customerId: number, businessUnitId: number, parameters: BaseRuleConfigParameterModel[], isVettingRule: boolean, isCriticalRule: boolean, isSoftRule: boolean, ruleConfigParameters: LookupValue[] }) {
        this.customerId = model.customerId;
        this.businessUnitId = model.businessUnitId;
        this.isReadOnly = model.isReadOnly;
        this.parameters = model.parameters;
        this.ruleConfigParameters = model.ruleConfigParameters;
        this.isSoftRule = model.isSoftRule;
        this.isCriticalRule = model.isCriticalRule;
        this.isVettingRule = model.isVettingRule;
    }

    startTracking(p: BaseRuleConfigParameterModel) {
        if (p.isAdvanced) {
            const dataType = this.ruleConfigParameters.filter(x => x.id == p.ruleConfigParameterId).map(x => x.additionalData.tableColumnDataTypeId)[0];
            if (dataType == DataType.Boolean) {
                for (const a of p.ruleConfigAdvancedParameters) {
                    a.value = a.value == "true" || a.value == "True" || a.value === true;

                }
            }
        }
        this.parameter = p;
        return new Promise<void>((resolve) => {
            this.clearChanges(p, () => {
                setTimeout(() => {
                    this.tackRuleParameters(p);
                    resolve();
                });
            });
        })
    }

    clearChanges(p: BaseRuleConfigParameterModel, callback: () => void = null) {
        if (this.uow) {
            this.uow.clearChanges();
            this.uow.dispose();
        }
        setTimeout(() => {
            this.uow = new UnitOfWorkObserver(this.bindingEngine, p);
            this.uow.startTracking();
            if (callback)
                callback();
        });
    }

    tackRuleParameters(rule: BaseRuleConfigParameterModel) {
        const ruleP = this.ruleConfigParameters.filter(x => rule.ruleConfigParameterId == x.id)[0];
        const dataType = ruleP.additionalData.tableColumnDataTypeId;
        for (const advanced of rule.ruleConfigAdvancedParameters) {
            try { advanced.whenClausesJson = advanced.whenClauses ? JSON.parse(advanced.whenClauses) : []; } catch {
                advanced.whenClausesJson = [];
            }

            if (dataType == DataType.Boolean) {
                advanced.value = this.getBooleanValue(advanced.value);
            }

            if ((dataType == DataType.Lookup || dataType == DataType.EntityFinder || dataType == DataType.GHGRatingSelector) && typeof (advanced.value) == "string") {

                try { advanced.value = advanced.value && JSON.parse("[" + advanced.value + "]") ? JSON.parse("[" + advanced.value + "]") : []; } catch {
                    advanced.value = [];
                }
            }
        }

        if (dataType == DataType.Boolean) {
            rule.value = this.getBooleanValue(rule.value);
        }

        if ((dataType == DataType.Lookup || dataType == DataType.EntityFinder || dataType == DataType.GHGRatingSelector) && typeof (rule.value) == "string") {
            try { rule.value = rule.value && JSON.parse("[" + rule.value + "]") ? JSON.parse("[" + rule.value + "]") : []; } catch {
                rule.value = [];
            }
        }
    }

    getBooleanValue(value) {
        return (value == "True" || value == true) ? true : (value == "False" || value == false) ? false : null;
    }

    close() {
        this.dialogClientService.hide();
    }

    async submit(p: BaseRuleConfigParameterModel) {
        this.applyRuleConfigParameters(p);
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            });
        });

        if (this.areThereUnsavedChanges()) {
            try {
                const allChanges = this.uow.getChanges();
                this.beforeSaveChanges(p, allChanges);
                const parameter = await this.manageRightShipUsersClientService.saveRuleConfigParameters({
                    rightShipRuleConfigParameterId: p["rightshipRuleConfigParameterId"],
                    customerRuleConfigParameterId: p["customerRuleConfigParameterId"],
                    businessUnitRuleConfigParameterId: p["businessUnitRuleConfigParameterId"],
                    changes: allChanges
                });
                p["rightshipRuleConfigParameterId"] = parameter["rightshipRuleConfigParameterId"];
                p["customerRuleConfigParameterId"] = parameter["customerRuleConfigParameterId"];
                p["businessUnitRuleConfigParameterId"] = parameter["businessUnitRuleConfigParameterId"];
                this.clearChanges(p, () => {
                    setTimeout(() => {
                        this.tackRuleParameters(p);
                    });
                });
                this.notificationClientService.show("Rule config parameters saved", "", NotificationType.Success);
            } catch {

                this.notificationClientService.show("Failed to save rule config parameters", "", NotificationType.Error);
            }

        } else {
            this.notificationClientService.show("No changes to save", "", NotificationType.Info);
        }
    }

    formatArrayValue(change: UnitOfWorkItem) {
        if (change.value && Array.isArray(change.value))
            change.value = change.value ? JSON.stringify(change.value).replace('[', '').replace(']', '') : null;
    }

    formatAdvancedParameterArrayValue(change: UnitOfWorkItem) {
        if(!change) return;
        if(typeof change === 'object' || Array.isArray(change)) {
            for (const advancedKey in change) {
                if (!change[advancedKey] || advancedKey == 'isArray') continue;
                this.formatArrayValue(change[advancedKey]);
                this.formatAdvancedParameterArrayValue(change[advancedKey]);
            }
        }
    }

    beforeSaveChanges(p: BaseRuleConfigParameterModel, changes: { [index: string]: UnitOfWorkItem }) {
        for (const key in changes) {
            const change = changes[key];
            this.formatArrayValue(change);

            if (key == "ruleConfigAdvancedParameters") {
                this.formatAdvancedParameterArrayValue(change);
            }
        }

        changes["ruleConfigParameterId"] = {
            action: UnitOfWorkAction.Modified,
            value: p.ruleConfigParameterId,
            oldValue: null
        };

        changes["vettingTypeId"] = {
            action: UnitOfWorkAction.Modified,
            value: p.vettingTypeId,
            oldValue: null
        };

        changes["isCritical"] = {
            action: UnitOfWorkAction.Modified,
            value: p.isCritical,
            oldValue: null
        };

        if (this.businessUnitId)
            changes["businessUnitId"] = {
                action: UnitOfWorkAction.Modified,
                value: this.businessUnitId,
                oldValue: null
            };

        if (this.customerId)
            changes["customerId"] = {
                action: UnitOfWorkAction.Modified,
                value: this.customerId,
                oldValue: null
            };
    }

    areThereUnsavedChanges() {
        const changes = this.uow.getChanges();
        return this.uow && this.uow.hasChanges() && !(Object.keys(changes).length == 1 && changes['identifier']);
    }

    applyRuleConfigParameters(p: BaseRuleConfigParameterModel) {
        for (const advanced of p.ruleConfigAdvancedParameters) {
            advanced.whenClauses = advanced.whenClausesJson ? JSON.stringify(advanced.whenClausesJson) : "";
        }
    }

    filtersHtml(item: BaseRuleConfigParameterModel, index: number = null): string {
        const dataType = this.ruleConfigParameters.filter(x => x.id == item.ruleConfigParameterId).map(x => x.additionalData.tableColumnDataTypeId)[0];
        const lookupMappedToProperty = this.ruleConfigParameters.filter(x => x.id == item.ruleConfigParameterId).map(x => x.additionalData.lookupMappedToProperty)[0];

        if (dataType == DataType.Date || dataType == DataType.DateTime) {
            if (!DateExpressionParser.isDateExpression(item.convertedValue)) {
                const x = new Date();

                let date = moment(item.convertedValue);
                if (!date.isValid())
                    date = moment(item.convertedValue, dataType === DataType.DateTime ? this.configService.config.common.defaultDateTimeFormat : this.configService.config.common.defaultDateFormat).add((x.getTimezoneOffset() / 60) * -1, "h");
                item.convertedValue = date.toISOString();
                item.value = item.convertedValue;
            }
        }

        const displayValue = this.getDisplayValue(item.isAdvanced && item.ruleConfigAdvancedParameters && item.ruleConfigAdvancedParameters.length > 0 ?
            item.ruleConfigAdvancedParameters[index ? index : 0].value : item.value, dataType);

        const isValueCollection = dataType == DataType.Lookup || dataType == DataType.EntityFinder;

        const binding: EditableFieldBinding = {
            query: `"${lookupMappedToProperty}"`,
            lookup: lookupMappedToProperty,
            isEditable: true,
            binding: "item.value",
            value: `"${lookupMappedToProperty}"`,
            dataType: dataType,
            parameter: "item.value",
            secondParameter: null,
            parameterObj: null,
            isNullable: true,
            isNotRangeDatePicker: true,
            displayValueBinding: `'${displayValue}'`,
            isDisabled: this.isReadOnly,
            isValueCollection: isValueCollection,
            isPropertyCollection: isValueCollection
        };

        if (dataType == DataType.GHGRatingSelector) {
            return `<div class="filter">
                        <div if.bind="!isReadOnly" class="filter-value row align-items-middle \${item.showGhgRatingFilter ? 'active' : '' }" click.delegate="ghgSelect($event, item)">
                            <span class="comp-vessel-type-value-text \${ghg.externalLabel && item.value && item.value.length > 0? '':'placeholder'}">
                            \${ghg.externalLabel && item.value && item.value.length > 0? ghg.externalLabel: "Select"}</span>
                            <i class="icon-dropdown material-icons">keyboard_arrow_down</i>
                        </div>
                        <ghg-rating-selector click.delegate="$event.stopPropagation()" show.bind="item.showGhgRatingFilter" selected-rating-weights.two-way="item.value" show-action-buttons.bind="false"
                        selected-items.two-way="item.ghgSelectedItems" view-model.ref="ghg" readonly.to-view="isReadOnly"></ghg-rating-selector>
                    </div>`;
        }

        if (dataType == DataType.DateRange) {
            return `
            <div class="row nowrap">
                <text-box class="col col-sm-4" value.two-way="item.value | dateRange :{type: 'date', item:item}" type="integer"
                    placeholder.bind="dvm.selectedItem.name" disbaled.to-view="isReadOnly"></text-box>
        
                <combo-box class="col col-sm-4" view-model.ref="dvm" selected-value.two-way="item.value | dateRange :{type: 'day', item:item}" selected-value-path="mapping"
                    display-member-path="name" set-default.bind="true" item-source.bind="itemSource" disbaled.to-view="isReadOnly"></combo-box>
            </div>
            `
        }

        return GridUtils.getControl(binding, false);
    }

    getLabel(ruleConfigParameterId: number) {
        const label = this.ruleConfigParameters.filter(x => x.id == ruleConfigParameterId).map(x => x.name)[0];
        return label ? label : "";
    }

    getDisplayValue(value: any, dataType: DataType) {
        if (dataType == DataType.Boolean)
            return value == true ? "Yes" : value == false ? "No" : "";
        else if (dataType == DataType.DateTime || dataType == DataType.Date)
            return moment(value).format(dataType == DataType.DateTime ? this.configService.config.common.defaultDateTimeFormat : this.configService.config.common.defaultDateFormat);

        return value;
    }

    isAdvancedChanged(item: BaseRuleConfigParameterModel) {
        if (item.isAdvanced && item.ruleConfigAdvancedParameters.length == 0) {
            this.addQuery(item);
        } else if (!item.isAdvanced) {
            item.ruleConfigAdvancedParameters.splice(0, item.ruleConfigAdvancedParameters.length);
        }
    }

    removeQuery(item: BaseRuleConfigParameterModel, index: number) {
        item.ruleConfigAdvancedParameters.splice(index, 1);
    }

    addQuery(item: BaseRuleConfigParameterModel) {
        item.ruleConfigAdvancedParameters.push({
            whenClauses: null,
            value: null,
            displayText: null,
            showDisplayText: false,
            whenClausesJson: [new QueryFilter()]
        });
    }

    clearConfigParams(item: BaseRuleConfigParameterModel, x) {
        if (x && x.value) {
            item.isAdvanced = false;
            item.useDefault = false;
            item.copyVettingRuleParameter = false;
            this.isAdvancedChanged(item);
        }
    }

    public getEntityFinderTypes(uri: string) {
        const queryLookup = QueryLookupsWithEF.filter(x => x.lookup == uri);
        if (queryLookup.length > 0) {
            const entity = AllEntityTypeConfigurationConstants.filter(x => x.uri == queryLookup[0].odataUri)[0];
            const clonedEntity = CloneHelper.clone(entity);
            return [clonedEntity];
        }

        const entity = AllEntityTypeConfigurationConstants.filter(x => x.uri == uri);
        return entity;
    }
}